import { BADGES, BLAZE_NAME, BLAZE_TENURE, blazeTenureTier, type PublicUser } from '@clanfire/shared';
import { Tooltip } from '@/components/ui/Popover';
import { formatDay } from '@/lib/time';
import { cn } from '@/lib/utils';

/** Hexagon art in public/badges (CEO, Alpha Tester, Blaze), drawn to look the same in both themes: no tile, glow or theme colours. */
function ImageBadge({ src, name, label }: { src: string; name: string; label: string }) {
  return (
    <Tooltip label={label}>
      <span role="listitem" className="inline-flex">
        <img src={src} alt={name} width={22} height={22} draggable={false} className="select-none" />
      </span>
    </Tooltip>
  );
}

/** A user's profile badges (CEO, Alpha Tester, Blaze), each with a tooltip saying what it is. */
export function ProfileBadges({ user, className }: { user: Pick<PublicUser, 'badges' | 'blazeSince'>; className?: string }) {
  // Founder has no art yet, so it isn't shown (the account keeps it, for its free Blaze months).
  const shown = BADGES.filter((b) => b.id !== 'founder' && user.badges.includes(b.id));
  if (!shown.length && !user.blazeSince) return null;
  return (
    <span className={cn('inline-flex items-center gap-1 align-middle', className)} role="list" aria-label="Badges">
      {shown.map((b) => (
        <ImageBadge key={b.id} src={`/badges/${b.id}.svg`} name={b.label} label={`${b.label}: ${b.description}`} />
      ))}
      {user.blazeSince && (
        <ImageBadge
          src="/badges/blaze.svg"
          name={BLAZE_NAME}
          label={`${BLAZE_NAME}: ${BLAZE_TENURE[blazeTenureTier(user.blazeSince)]!.label}, since ${formatDay(user.blazeSince)}.`}
        />
      )}
    </span>
  );
}
